/**
 * LegalPage — Iter 67aj.
 *
 * Shared component used by both /terms and /privacy. Pulls the saved HTML
 * (admin-managed via Settings → Site settings) from `GET /api/site-settings`
 * and renders it inside a max-w-3xl prose container with a "Last updated"
 * stamp. When the admin hasn't saved any content yet we render a friendly
 * placeholder so the page never looks broken.
 *
 * Security note: we render trusted, admin-authored HTML. Sanitisation is the
 * admin form's responsibility — this is the same model platforms like Stripe
 * and Notion use for their docs pages.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ScrollText, Sparkles } from "lucide-react";

export default function LegalPage({ kind }) {
  // Iter 67ea — localized title/description per legal kind.
  const metaTitleKey = kind === "terms" ? "meta.terms.title"
    : kind === "privacy" ? "meta.privacy.title"
    : "meta.about.title";
  const metaDescKey = kind === "terms" ? "meta.terms.description"
    : kind === "privacy" ? "meta.privacy.description"
    : "meta.about.description";
  usePageMeta(metaTitleKey, metaDescKey);
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Iter 69 — pass the active language so the backend returns AI-
    // translated terms/privacy/about HTML cached on the singleton doc.
    setLoading(true);
    api.get(`/site-settings?lang=${encodeURIComponent(lang)}`)
      .then((r) => setSettings(r.data))
      .catch((err) => {
        console.warn("[LegalPage] failed to load /site-settings:", err);
        setSettings({});
      })
      .finally(() => setLoading(false));
  }, [lang]);

  // Iter 69 — three kinds: terms, privacy, about. Each pulls a different
  // HTML field + updated_at timestamp from /site-settings. Title and pill
  // copy go through i18next so the page header matches the renter's
  // active language (the body HTML is LLM-translated by the backend).
  const KIND_META = {
    terms:   { htmlKey: "terms_html",   stampKey: "terms_updated_at",   titleKey: "meta.terms.title",   pillKey: "legalPage.pillLegal" },
    privacy: { htmlKey: "privacy_html", stampKey: "privacy_updated_at", titleKey: "meta.privacy.title", pillKey: "legalPage.pillLegal" },
    about:   { htmlKey: "about_html",   stampKey: "about_updated_at",   titleKey: "meta.about.title",   pillKey: "legalPage.pillAbout" },
  };
  const meta = KIND_META[kind] || KIND_META.terms;
  const title = t(meta.titleKey);
  const pill  = t(meta.pillKey);
  const html = settings?.[meta.htmlKey];
  const updatedAt = settings?.[meta.stampKey];

  // Iter 67at — DOMPurify defence-in-depth even though admin authors are
  // trusted; protects against compromised admin accounts and impersonation
  // sessions, and keeps Snyk/SonarQube quiet about `dangerouslySetInnerHTML`.
  const sanitizedHtml = useMemo(() => {
    if (!html || !html.trim()) return "";
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  }, [html]);

  return (
    <div className="min-h-screen flex flex-col" data-testid={`legal-page-${kind}`}>
      <Header />
      <main className="flex-1 bg-neutral-50/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 mb-2 text-neutral-500">
            <ScrollText className="h-5 w-5" />
            <div className="text-xs uppercase tracking-[0.18em] font-bold">{pill}</div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold mb-3" data-testid={`legal-page-${kind}-title`}>{title}</h1>
          {updatedAt && (
            <p className="text-xs text-neutral-500 mb-10" data-testid={`legal-page-${kind}-updated`}>
              {t("legalPage.lastUpdated")} <strong>{new Date(updatedAt).toLocaleDateString(lang, { year: "numeric", month: "long", day: "numeric" })}</strong>
            </p>
          )}

          {/* Iter 69 — Transparency note: when the renter is on a non-EN
              locale we serve LLM-translated copy. Surface that openly so
              renters know to fall back to the English source if they need
              the canonical legal text (regulator/DPO-friendly). */}
          {settings?.resolved_lang && settings.resolved_lang !== "en" && sanitizedHtml && (
            <div
              className="mb-10 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900"
              data-testid={`legal-page-${kind}-ai-notice`}
            >
              <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                {t("legalPage.aiNotice")}{" "}
                <a
                  href={`${typeof window !== "undefined" ? window.location.pathname : ""}?lang=en`}
                  onClick={(e) => {
                    // Stay on-page; switch the language back to EN without
                    // forcing a full reload. The query param is purely a
                    // semantic anchor for screen readers / shareable URL.
                    e.preventDefault();
                    i18n.changeLanguage("en");
                    try { localStorage.setItem("rentalcar_lang", "en"); } catch { /* private mode */ }
                  }}
                  className="font-semibold underline underline-offset-2"
                  data-testid={`legal-page-${kind}-ai-notice-en-link`}
                >
                  {t("legalPage.viewEnglish")}
                </a>
              </span>
            </div>
          )}

          {loading ? (
            <div className="space-y-3" data-testid={`legal-page-${kind}-loading`}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={`legal-skeleton-${kind}-${i}`} className="h-3 rounded-full bg-neutral-100 animate-pulse" style={{ width: `${70 + (i % 3) * 10}%` }} />
              ))}
            </div>
          ) : sanitizedHtml ? (
            <article
              className="legal-content text-neutral-800 leading-relaxed [&_h1]:font-display [&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1]:mt-10 [&_h1]:mb-4 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 [&_li]:my-1 [&_a]:text-blue-600 [&_a]:underline [&_strong]:text-neutral-900 [&_strong]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-neutral-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-neutral-600 [&_hr]:my-8 [&_hr]:border-border"
              data-testid={`legal-page-${kind}-content`}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-white px-6 py-12 text-center text-neutral-500" data-testid={`legal-page-${kind}-empty`}>
              <p className="text-sm">
                {t("legalPage.empty", { title })}
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
